import * as React from 'react';

import { GradeModifiedEventAdapter } from '~analytics/adapters/GradeModifiedEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { useResultState } from '~teacher/components/ActivityResult/ActivityResultProvider';
import { useStudentSortProvider } from '~teacher/components/ActivityResult/context/StudentSortContext';
import { NormalizedQuestion } from '~teacher/components/ActivityResult/util/normalizeQuestions';
import { NormalizedResponse } from '~teacher/components/ActivityResult/util/normalizeResponses';
import { NormalizedStudent } from '~teacher/components/ActivityResult/util/normalizeStudent';
import { usePaywallReducer } from '~teacher/components/PaywallDialog';
import { Activity } from '~teacher/models/activities';
import { proCheck } from '~teacher/util/proCheck';

import { useStudentScoreProvider } from './StudentScoreContext';
import { StudentScoreModal } from './StudentScoreModal';
import { StudentScorePaywall } from './StudentScorePaywall';
import { findResponse, findStudentScore } from './utils';

interface StudentScoreProps {
  activity: Activity;
  questions: NormalizedQuestion;
  responses: NormalizedResponse;
  students: NormalizedStudent;
}

export function StudentScore({
  activity,
  questions,
  responses,
  students,
}: StudentScoreProps) {
  const { questionId, studentId, selectStudentQuestion } =
    useStudentScoreProvider();
  const studentSortValue = useStudentSortProvider();
  const { questionScores } = useResultState();
  const [{ open: paywallOpen }, dispatch] = usePaywallReducer();
  const canManuallyGrade = proCheck.canManuallyGrade();

  const sortedStudentIds = studentSortValue.sortedStudentIds.filter(
    (studentId) => !!findResponse({ questionId, responses, studentId })
  );

  const handleDone = React.useCallback(
    () => selectStudentQuestion({ studentId: null, questionId: null }),
    [selectStudentQuestion]
  );

  const handleOpenPaywall = React.useCallback(() => {
    if (!canManuallyGrade) {
      dispatch({
        type: 'OPEN',
        touch: [PaywallScreens.ResultsAddManualGrade],
      });
    }
  }, [canManuallyGrade, dispatch]);

  if (!questionScores) return null;

  const shortAnswerResponse = findResponse({
    questionId: questionId,
    responses: responses,
    studentId: studentId,
  });

  const studentScore = findStudentScore({
    questionId: questionId,
    studentId: studentId,
    questionScores: questionScores,
  });

  const questionScore = questionScores[questionId];

  const question = questions.entities[questionId];
  const student = students.entities[studentId];

  if (!questionId && !studentId) return null;
  if (!question) return null;
  if (!student) return null;

  const onScoreSave = (analyticsData) => {
    new GradeModifiedEventAdapter(activity.isRostered, {
      ...analyticsData,
      activity_type: activity.activityType,
      activity_student_count: Object.keys(students.entities).length,
      activity_question_count: Object.keys(questions.entities).length,
      activity_end_date: activity.endTime,
      question_type: question.type,
    }).sendAnalytics();
  };

  return (
    <>
      <StudentScoreModal
        studentIds={sortedStudentIds}
        activityInstanceId={activity.id}
        canManuallyGrade={canManuallyGrade}
        isRostered={activity.isRostered}
        handleModalCancel={handleDone}
        questionScore={questionScore}
        question={question}
        selectStudentQuestion={selectStudentQuestion}
        response={shortAnswerResponse}
        student={student}
        studentScore={studentScore}
        maybeOpenPaywall={handleOpenPaywall}
        onSave={onScoreSave}
      />
      <StudentScorePaywall
        open={paywallOpen}
        onClose={() => dispatch({ type: 'CLOSE' })}
      />
    </>
  );
}
